import React, { useEffect, useState } from 'react';
import { LiaCheckCircle } from 'react-icons/lia';
import { FiInfo } from 'react-icons/fi';

const formatNumber = (num) => {
  const number = typeof num === 'string' ? parseFloat(num) : num;
  return number.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const RoomPayment = () => {
  const [bookingData, setBookingData] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [baseAmount, setBaseAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [cgstAmount, setCgstAmount] = useState(0);
  const [sgstAmount, setSgstAmount] = useState(0);
  const [hotelProperty, setHotelProperty] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const loadData = () => {
    try {
      // Get room service billing details
      const storedBillingData = JSON.parse(sessionStorage.getItem('billingDetails'));

      // Get selected service details
      const storedServiceData = JSON.parse(sessionStorage.getItem('selectedService'));

      // Get booking details for guest info only
      const storedBookingData = JSON.parse(sessionStorage.getItem('bookingDetails'));

      // Load hotel property from localStorage
      const storedHotelProperty = localStorage.getItem('hotelPropertyCache');
      if (storedHotelProperty) {
        try {
          const parsed = JSON.parse(storedHotelProperty);
          setHotelProperty(parsed);
        } catch (e) {
          console.error('Error parsing hotel property:', e);
        }
      }

      // Process billing details
      if (storedBillingData) {
        const finalAmount = storedBillingData.finalamount;
        const baseAmount = (finalAmount / 1.12).toFixed(2);
        const taxAmount = (finalAmount - baseAmount).toFixed(2);
        const cgstAmount = (baseAmount * 0.06).toFixed(2);
        const sgstAmount = (baseAmount * 0.06).toFixed(2);

        setFinalAmount(finalAmount);
        setBaseAmount(baseAmount);
        setTaxAmount(taxAmount);
        setCgstAmount(cgstAmount);
        setSgstAmount(sgstAmount);
        setBillingDetails(storedBillingData);
      }

      // Set selected service
      if (storedServiceData) {
        setSelectedService(storedServiceData);
      }

      // Get guest info from booking details
      if (storedBookingData) {
        const primaryBooking =
          storedBookingData.primaryBooking ||
          (storedBookingData.bookings && storedBookingData.bookings[0]);

        if (primaryBooking) {
          setBookingData({
            firstname: primaryBooking.guest_name?.split(' ')[0] || '',
            lastname: primaryBooking.guest_name?.split(' ').slice(1).join(' ') || '',
            phonenumber: primaryBooking.guest_phone || '',
            emailid: primaryBooking.guest_email || '',
          });
        }
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDone = () => {
    // Clear room service related data
    sessionStorage.removeItem('billingDetails');
    sessionStorage.removeItem('selectedService');

    // Navigate to welcome page
    window.location.href = '/reservation/options/booking/welcome';
  };

  if (!billingDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="text-gray-700 font-medium">Loading payment details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full"></div>
            </div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6 shadow-lg">
                <LiaCheckCircle className="text-gray-600 w-12 h-12" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-3 tracking-tight">
                Room Service Booking Successful!
              </h2>
              <p className="text-green-50 text-lg max-w-2xl mx-auto">
                {selectedService?.roomservice_name || 'Your room service'} has been booked
                successfully.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
            {/* Left Column - Service Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Service Summary Card */}
              {selectedService && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mr-2"></span>
                      Service Details
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="bg-gray-50 rounded-lg p-5 mb-4">
                      <p className="text-xs uppercase tracking-wider text-gray-900 mb-1">
                        Service Name
                      </p>
                      <p className="text-xl font-semibold text-gray-900">
                        {selectedService.roomservice_name || selectedService.name}
                      </p>
                    </div>
                    {selectedService.description && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Description
                        </p>
                        <p className="text-gray-700">{selectedService.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Guest Information Card */}
              {bookingData && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mr-2"></span>
                      Guest Information
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-50 rounded-lg p-5 mb-4">
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                        Guest Name
                      </p>
                      <p className="text-xl font-semibold text-gray-900 capitalize">
                        {bookingData.firstname} {bookingData.lastname}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Contact Number
                        </p>
                        <p className="text-lg font-medium text-gray-900">
                          {bookingData.phonenumber}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Email</p>
                        <p className="text-lg font-medium text-gray-900 break-all">
                          {bookingData.emailid}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Payment Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 sticky top-8 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-6 pb-3 border-b border-gray-700">
                  Payment Summary
                </h3>

                <div className="space-y-4">
                  <div className="flex justify-between items-center text-gray-300">
                    <span>Subtotal</span>
                    <span className="font-medium text-white">₹{formatNumber(baseAmount)}</span>
                  </div>

                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-300">Taxes (GST)</span>
                      <div className="group relative inline-block">
                        <FiInfo className="text-gray-400 hover:text-white cursor-pointer transition-colors" />
                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-56 bg-white rounded-xl shadow-2xl p-4 border border-gray-200 z-10">
                          <div className="text-sm space-y-2">
                            <div className="flex justify-between text-gray-700">
                              <span>CGST</span>
                              <span className="font-medium">₹{formatNumber(cgstAmount)}</span>
                            </div>
                            <div className="flex justify-between text-gray-700">
                              <span>SGST</span>
                              <span className="font-medium">₹{formatNumber(sgstAmount)}</span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-gray-200 text-xs text-gray-500">
                              Total Tax: ₹{formatNumber(taxAmount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium text-white">₹{formatNumber(taxAmount)}</span>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">Total Amount</span>
                      <span className="text-xl font-bold text-white">
                        ₹{formatNumber(finalAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <button
                    onClick={handleDone}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl"
                  >
                    Continue to Welcome
                  </button>

                  {hotelProperty?.data?.phone && (
                    <div className="text-center mt-4 p-3 bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-400">Need assistance?</p>
                      <p className="text-sm font-medium text-white">{hotelProperty.data.phone}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 text-center border-t border-gray-700">
            {hotelProperty?.data?.propertyname && (
              <p className="text-white font-medium mb-2">{hotelProperty.data.propertyname}</p>
            )}
            <p className="text-gray-300">
              Thank you for choosing our room service. We hope you enjoy our experience!
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RoomPayment;

// import React, { useEffect, useState } from "react";
// import qr from "../../assets/QR.png";
// import { LiaCheckCircle } from "react-icons/lia";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack, IoMdPrint } from "react-icons/io";

// import { FiInfo } from "react-icons/fi"; // Added info icon

// const formatNumber = (num) => {
//   const number = typeof num === "string" ? parseFloat(num) : num;
//   return number.toLocaleString("en-IN", {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });
// };

// const RoomPayment = () => {
//   const [bookingData, setBookingData] = useState(null);
//   const [billingDetails, setBillingDetails] = useState(null);
//   const [baseAmount, setBaseAmount] = useState(0);
//   const [taxAmount, setTaxAmount] = useState(0);
//   const [finalAmount, setFinalAmount] = useState(0);
//   const [cgstAmount, setCgstAmount] = useState(0);
//   const [sgstAmount, setSgstAmount] = useState(0);

//   useEffect(() => {
//     const storedBookingData = JSON.parse(
//       sessionStorage.getItem("bookingDetails")
//     );
//     const storedBillingData = JSON.parse(
//       sessionStorage.getItem("billingDetails")
//     );

//     if (storedBillingData) {
//       const finalAmount = storedBillingData.finalamount;
//       const baseAmount = (finalAmount / 1.12).toFixed(2);
//       const taxAmount = (finalAmount - baseAmount).toFixed(2);
//       const cgstAmount = (baseAmount * 0.06).toFixed(2);
//       const sgstAmount = (baseAmount * 0.06).toFixed(2);

//       setFinalAmount(finalAmount);
//       setBaseAmount(baseAmount);
//       setTaxAmount(taxAmount);
//       setCgstAmount(cgstAmount);
//       setSgstAmount(sgstAmount);
//       setBillingDetails(storedBillingData);
//     }

//     if (storedBookingData) {
//       const primaryBooking =
//         storedBookingData.primaryBooking ||
//         (storedBookingData.bookings && storedBookingData.bookings[0]);

//       if (primaryBooking) {
//         setBookingData({
//           checkindate: primaryBooking.checkin_date,
//           checkoutdate: primaryBooking.checkout_date,
//           roomtypename: primaryBooking.room_type?.name,
//           numberofguests: primaryBooking.number_of_guests,
//           firstname: primaryBooking.guest_name.split(" ")[0],
//           lastname: primaryBooking.guest_name.split(" ").slice(1).join(" "),
//           phonenumber: primaryBooking.guest_phone,
//           emailid: primaryBooking.guest_email,
//           booking_type: "Room Service",
//         });
//       }
//     }
//   }, []);

//   const handleDone = () => {
//     window.location.href = "reservation/options/booking/welcome";
//   };

//   const handlePrint = () => {
//     window.print();
//   };

//   if (!bookingData || !billingDetails) {
//     return (
//       <div className="flex items-center justify-center h-screen text-gray-700">
//         Loading booking details...
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gray-50">
//       <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
//         <div className="bg-white shadow-xl rounded-lg overflow-hidden">
//           <div className="bg-gray-100 border-l-4 border-black p-6 text-center relative">
//             <div className="absolute right-8 top-8 border-2 border-white p-1 bg-white">
//               <img src={qr} alt="QR Code" className="w-24 h-24" />
//               <p className="text-xs text-black mt-1">Scan for details</p>
//             </div>
//             <LiaCheckCircle className="text-green-400 w-14 h-14 mb-4 mx-auto" />
//             <h2 className="text-3xl font-bold mb-2">
//               Room Service Payment Successful
//             </h2>
//             <p className="text-black">
//               Your room service has been booked successfully. Enjoy your
//               experience!
//             </p>
//           </div>

//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
//             <div className="lg:col-span-2 space-y-8">
//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Booking Summary
//                 </h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-in Date</p>
//                       <p className="font-medium">{bookingData.checkindate}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Number of Guests</p>
//                       <p className="font-medium">
//                         {bookingData.numberofguests}
//                       </p>
//                     </div>
//                   </div>
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-out Date</p>
//                       <p className="font-medium">{bookingData.checkoutdate}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Guest Information
//                 </h3>
//                 <div className="space-y-4">
//                   <div>
//                     <p className="text-sm text-gray-500">Full Name</p>
//                     <p className="font-medium text-transform:capitalize">
//                       {bookingData.firstname} {bookingData.lastname}
//                     </p>
//                   </div>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <p className="text-sm text-gray-500">Contact Number</p>
//                       <p className="font-medium">{bookingData.phonenumber}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Email</p>
//                       <p className="font-medium">{bookingData.emailid}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="bg-gray-50 rounded-lg p-6 h-fit sticky top-8">
//               <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                 Payment Summary
//               </h3>
//               <div className="space-y-4">
//                 <div className="flex justify-between">
//                   <span className="text-gray-600">Subtotal:</span>
//                   <span className="font-medium">
//                     ₹{formatNumber(baseAmount)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between items-center">
//                   <div className="flex items-center">
//                     <span className="text-gray-600 mr-1">Taxes </span>
//                     <div className="group relative inline-block">
//                       <FiInfo className="text-gray-400 hover:text-gray-600 cursor-pointer" />
//                       <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-48 bg-white shadow-lg rounded-lg p-3 border border-gray-200 z-10">
//                         <div className="text-xs text-gray-600">
//                           <div className="flex justify-between">
//                             <span>CGST (6%):</span>
//                             <span>₹{formatNumber(cgstAmount)}</span>
//                           </div>
//                           <div className="flex justify-between mt-1">
//                             <span>SGST (6%):</span>
//                             <span>₹{formatNumber(sgstAmount)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   <span className="font-medium">
//                     ₹{formatNumber(taxAmount)}
//                   </span>
//                 </div>
//                 <div className="pt-4 border-t border-gray-200">
//                   <div className="flex justify-between items-center">
//                     <span className="font-semibold">Total:</span>
//                     <span className="text-2xl font-bold">
//                       ₹{formatNumber(finalAmount)}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-8 space-y-4">
//                 <button
//                   onClick={handleDone}
//                   className="w-full flex items-center justify-center bg-white border border-black text-black py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
//                 >
//                   Continue to Welcome
//                 </button>
//               </div>
//             </div>
//           </div>

//           <div className="bg-gray-100 p-6 text-center border-t border-gray-200">
//             <p className="text-gray-600">
//               Need assistance? Contact our concierge at
//               roomservice@grandhotel.com
//             </p>
//             <p className="text-gray-600 mt-1">
//               Enjoy your room service during your stay!
//             </p>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default RoomPayment;
